import React, { useState, useEffect } from 'react';
import config from './../../../config';
import ChartDummy from './ChartDummy/ChartDummy';
import ClickToChartImage from './ClickToChartImage/ClickToChartImage';
import ChartActions from './ChartActions/ChartActions';
import { ChartContainer, ChartWrapper, MobileActionsWrapper, ValuePropositionContainer } from './Chart.styled';
import { connect, useDispatch } from 'react-redux';
import { template_chart_set_title } from '../../../redux/reducers/templateSlice';
import { useWindowSize } from '../../../helpers/hooks/useWindowSize';
import { Button, InlinePrompt, MobilePromptBody, PromptBody, LoginQuestionAndLink } from '@emarketer/componentlibrary';
import { COLOR } from '../../../theme/constants';
import { checkPremiumForecast } from '../../../redux/reducers/premiumSlice';
import { getUtmsForProPlus } from '../../../helpers/UtmHelper';

export interface IMetricChart {
  showLimitedAccess: boolean;
  showLogin: boolean;
  chartYear: string;
  chartType: string;
  id: string;
  name: string;
  titleInChart: string;
  setTitleInChart;
  subtitleInChart: string;
  geography: string;
  period: number;
  publicationDate: string;
  lastModifiedTimestamp: string;
  source: string;
  headers: string[];
  charted: object;
  data: object;
  metricScaleMode: string;
  chartedLength: number;
  logoType: string;
  collectionId: string;
  isLoaded: boolean;
  note: string;
  onDoesMyCompanySubscribe: any;
  displayPremiumFeatures: boolean;
}

export interface IChartData {
  id: number;
  IsCharted: boolean;
  type: string;
  values: number[];
  years: string[];
  formattedName: string;
  formattedValues: string[];
  hasData: boolean;
  color: string;
}

function mapStateToProps(state) {
  return {
    id: state.template.id,
    collectionId: state.collection.id,
    isLoaded: state.template.loaded,
    name: state.template.name,
    titleInChart: state.template.titleInChart,
    subtitleInChart: state.template.subtitleInChart,
    geography: state.template.geography,
    period: state.template.period,
    publicationDate: state.template.publicationDate,
    lastModifiedTimestamp: state.template.lastModifiedTimestamp,
    source: state.template.source,
    headers: state.template.headers,
    charted: state.template.charted,
    data: state.template.data,
    metricScaleMode: state.template.metricScaleMode,
    logoType: state.template.logoType,
    chartYear: state.template.chartYear,
    chartType: state.template.chartType,
    showLogin: !state.auth.authenticated && state.collection.defaultTemplateId !== state.template.id,
    showLimitedAccess:
      state.auth.authenticated &&
      state.collection.defaultTemplateId !== state.template.id &&
      state.collection.type === 'Benchmarks' &&
      !state.auth.Benchmarks,
    note: state.template.note,
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  return {
    setTitleInChart: (title) => {
      dispatch(template_chart_set_title(title));
    },
  };
}

const isMobileDevice = () => {
  return window.innerWidth <= 823;
};

const Chart: React.FC<IMetricChart> = (props) => {
  const templateChart = props;
  const browser = useWindowSize();
  const dispatch = useDispatch();
  const { id, name, charted, showLogin, collectionId, isLoaded, onDoesMyCompanySubscribe, displayPremiumFeatures } =
    props;
  const [loginUrl, setLoginUrl] = useState(config.subscriptionsLoginUrl);

  useEffect(() => {
    dispatch(checkPremiumForecast({ collectionId, templateId: id }));
  }, [id]);

  const valuePropositionTitle = displayPremiumFeatures
    ? 'Access our PRO+ Forecasts'
    : 'Access our Proprietary Forecasts';
  const valuePropositionCopy = displayPremiumFeatures
    ? 'Get to decision-making faster by unlocking access to new data sets, deeper insights, and flexible data visualizations.'
    : 'Size the next five years of your market and inform your strategic decision-making with thousands of forward-looking estimates to near pinpoint accuracy.';

  useEffect(() => {
    if (config.podRedirectionServiceUrl && config.subscriptionsLoginUrl === loginUrl) {
      // Get the proper redirect URL from the serivce
      fetch(`${config.podRedirectionServiceUrl}${window.location.href}`)
        .then((result) => result.text())
        .then((returnUrl) => {
          if (returnUrl) {
            setLoginUrl(`${config.subscriptionsLoginUrl}?returnUrl=${encodeURIComponent(JSON.parse(returnUrl))}`);
          }
        });
    }
  }, [loginUrl, config.podRedirectionServiceUrl]);

  const isAnythingCharted = () => {
    let chartAvailable: boolean = false;
    if (charted) {
      Object.keys(charted).forEach((row) => {
        if (charted[row].IsCharted) {
          chartAvailable = true;
        }
      });
      return chartAvailable;
    }
    return chartAvailable;
  };

  const chartActions = () => {
    return (
      <ChartActions
        id={templateChart.id}
        allowExport={true}
        name={templateChart.name}
        chartType={templateChart.chartType}
        collectionId={collectionId}
      />
    );
  };

  const chartDisplay = () => {
    if (typeof window !== 'undefined') {
      const isMobile = isMobileDevice();
      if (showLogin && isLoaded) {
        return (
          <ValuePropositionContainer className="row">
            <InlinePrompt
              title={
                <LoginQuestionAndLink
                  linkUrl={loginUrl}
                  loginAnalytics="Forecasts-ValueProposition-Login"
                  loginLinkId="Forecasts-ValueProposition-Login"
                />
              }
              isMobile={isMobile}
              footerButtons={[
                <Button
                  key="demo"
                  as="a"
                  size="m"
                  href={`${config.iiHomeUrl}/pro-plus/${getUtmsForProPlus()}`}
                  dataAnalytics="Forecasts-ValueProposition-GetADemo"
                  id="Forecasts-ValueProposition-GetADemo"
                >
                  Get a Demo
                </Button>,
                <Button
                  key="dmcs"
                  as="button"
                  size="m"
                  href={`${config.iiHomeUrl}/become-a-client/`}
                  dataAnalytics="Forecasts-ValueProposition-DoesMyCompanySubscribe?"
                  id="Forecasts-ValueProposition-DoesMyCompanySubscribe?"
                  buttonType={isMobile ? `lightgray` : `white`}
                  callback={() => {
                    onDoesMyCompanySubscribe();
                  }}
                >
                  Does my company Subscribe?
                </Button>,
              ]}
              customBorder={isMobile ? `border: 1px solid ${COLOR.GRAY200}; border-radius:8px;` : ``}
              footerBackgroundColor={isMobile ? `${COLOR.WHITE};` : ``}
              headerTextAlignment="text-align:center;"
            >
              {isMobile ? (
                <MobilePromptBody.TitleMessageImage
                  header={valuePropositionTitle}
                  image={
                    <div style={{ backgroundColor: COLOR.GRAY50, padding: '16px' }}>
                      <img src="/forecasts/images/mobile-lock.svg" width={94} height={94} alt="Lock" />
                    </div>
                  }
                  message={valuePropositionCopy}
                />
              ) : (
                <PromptBody.TitleMessageImage
                  header={valuePropositionTitle}
                  image={<img src="/forecasts/images/lock.svg" alt="Lock" />}
                  message={valuePropositionCopy}
                />
              )}
            </InlinePrompt>
          </ValuePropositionContainer>
        );
      }

      if (isLoaded) {
        return isAnythingCharted() ? (
          <ChartContainer>
            {/* <ChartTitleEditor titleInChart={templateChart.titleInChart} setTitle={setTitleInChart} /> */}
            {/* <ChartDisplay
                    browser={browser}
                    subtitle={formattedChart.subtitle}
                    credits={formattedChart.credits}
                    formattedDates={formattedChart.formattedDates}
                    seriesData={formattedChart.rawSeriesData}
                    ChartType={templateChart.chartType}
                    TitleInChart={templateChart.titleInChart}
                    Scale={templateChart.metricScaleMode}
                    eMChartIndex={formattedChart.eMChartIndex}
                    logoType={templateChart.logoType}
                 /> */}
            {!browser.isMobile ? chartActions() : null}
            {/* {config.enablePremiumFeatures && !browser.isMobile ? <PremiumPanel /> : null}  */}
            <ClickToChartImage />
          </ChartContainer>
        ) : (
          <ChartDummy id={id} name={name} />
        );
      }
    }

    return <div style={{ height: '450px', width: '100%' }} />;
  };

  return (
    <>
      <ChartWrapper>{chartDisplay()}</ChartWrapper>
      <MobileActionsWrapper>
        {/* {browser.isMobile ? chartActions() : null}
            {config.enablePremiumFeatures && browser.isMobile ? <PremiumPanel /> : null}*/}
      </MobileActionsWrapper>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Chart);
